import { ActionType } from '../dtos/action'
import { MessageType, SenderType } from '../dtos/message'
import { ProfileType } from '../dtos/profile'
import { ReportType } from '../dtos/report'
import { generateId } from '../uuid/uuid'

function removeDays(now: Date, number: number) {
  return now.getTime() - number * 60 * 60 * 24 * 1000
}

const now = new Date()

const minute = 1_000 * 60

export function mockProfileData(id: ProfileType['id']): ProfileData {
  const v = profileList[Number(id) - 1]
  if (!v) throw new Error(`No mock data for profile id ${id}`)
  return v
}

type ProfileData = {
  profile: ProfileType
  reports: ReportType[]
  actionHistoryList: ActionType[]
  messages: (sender: SenderType) => MessageType[]
}
const profile1: ProfileData = {
  profile: {
    id: '1',
    firstName: 'Abdoulaziz',
    lastName: 'Diallo',
    age: 20,
    education: 'Junior high',
    location: 'Saint-Denis',
    gender: 'male',
    lastUpdate: '2 weeks ago',
    status: 'priority',
  },

  reports: [
    {
      id: generateId(),
      content:
        'Abdoulaziz was feeling a bit down. He went to see Wimoov but they couldn’t help him because he needs a commercial license if he wants to be a truck driver. He still hasn’t been to the doctor for his back pain issues.',
      createdAtTime: removeDays(now, 7),
    },
    {
      id: generateId(),
      content:
        'Abdoulaziz went to the budgeting workshop and found it very useful. But he’s unsure about whether he can keep working in the food service industry because of his back pain and may need to look into other options.',
      createdAtTime: removeDays(now, 14),
    },
    {
      id: generateId(),
      content:
        'His back keeps hurting when he stays upright too long and it’s been making his waiter job very painful. I advised him to go see a doctor to get it checked out.',
      createdAtTime: removeDays(now, 21),
    },
    {
      id: generateId(),
      content:
        'Abdoulaziz is doing more shifts at his restaurant but has trouble saving money. He’s had trouble at home because of his mom’s health issues and his siblings and didn’t have the time to look into training options.',
      createdAtTime: removeDays(now, 4 * 7),
    },
    {
      id: generateId(),
      content:
        'First meeting with Abdoulaziz. He has been working odd jobs for the past few years, currently is a waiter in a restaurant. He’s also worked in retail at Super U but doesn’t like it. He wants to become a truck or bus driver but doesn’t have a license and doesn’t have the money to get trained.',
      createdAtTime: removeDays(now, 5 * 7),
    },
  ],
  actionHistoryList: [
    {
      id: generateId(),
      title: 'Book an appointment with his GP',
      content:
        "Go to the doctor's to get his recurring back pain issues checked out. Abdoulaziz's health issues impact his ability to work physical jobs.",
      categories: ['health'],
      status: 'priority',
      durationInHours: 1,
      isBeneficiaryTask: true,
      generationId: '123',
      type: 'task',
    },
    {
      id: generateId(),
      title: 'Make a list of less physically demanding side jobs',
      content:
        'Encourage Abdoulaziz to list potential jobs he could see himself working, in particular those that are less physically demanding. He may use tools such as Jobflix to explore these options.',
      categories: ['employment'],
      status: 'overdue',
      durationInHours: 1,
      isBeneficiaryTask: true,
      generationId: '123',
      type: 'task',
    },
    {
      id: generateId(),
      title: "Get in touch with Wimoov to help fund his drivers' license",
      content: 'The Wimoov nonprofit helps at-risk youth find solutions for their professional transportation needs',
      categories: ['financial'],
      status: 'overdue',
      durationInHours: 1,
      isBeneficiaryTask: true,
      generationId: '123',
      type: 'task',
    },
    {
      id: generateId(),
      title: "Attend CEJ's Budgeting 101 workshop",
      content:
        'Attend the Budgeting 101 workshop on Wednesday, June 26 at 2pm to help learn the basics of personal budgeting. This can help Abdoulaziz as he wants to gain more independence.',
      categories: ['financial'],
      status: 'up-to-date',
      durationInHours: 3,
      isBeneficiaryTask: true,
      generationId: '123',
      type: 'task',
    },
  ],
  messages: (sender) => [
    {
      createdAtTime: removeDays(now, 5),
      id: generateId(),
      content: 'Hello',
      sender: 'youth',
      type: 'message',
      isSender: sender === 'youth',
    },
    {
      createdAtTime: removeDays(now, 5) + minute,
      id: generateId(),
      content: 'Can I ask you something?',
      sender: 'youth',
      type: 'message',
      isSender: sender === 'youth',
    },
    {
      createdAtTime: removeDays(now, 5) + 3 * minute,
      id: generateId(),
      content: 'Hi Abdoulaziz, of course',
      sender: 'coach',
      type: 'message',
      isSender: sender === 'coach',
    },
    {
      createdAtTime: removeDays(now, 5) + 10 * minute,
      id: generateId(),
      content:
        "It didn't go went well with Wimoov, they dont' fund professional licenses, do you have another solution?",
      sender: 'youth',
      type: 'message',
      isSender: sender === 'youth',
    },
    {
      createdAtTime: removeDays(now, 5) + 13 * minute,
      id: generateId(),
      content:
        "Yes, we will discuss it next week during our meeting. In the meantime, take some rest and don't stress your back.",
      sender: 'coach',
      type: 'message',
      isSender: sender === 'coach',
    },
    {
      createdAtTime: removeDays(now, 5) + 20 * minute,
      id: generateId(),
      content: 'Thx I will!',
      sender: 'youth',
      type: 'message',
      isSender: sender === 'youth',
    },
  ],
}
const profile2: ProfileData = {
  profile: {
    id: '2',
    firstName: 'Sophia',
    lastName: 'Moreau',
    age: 19,
    education: 'High School Dropout',
    location: 'Maison-Alfort',
    gender: 'female',
    lastUpdate: '2 weeks ago',
    status: 'overdue',
  },

  reports: [
    {
      id: generateId(),
      content:
        'Sophia missed her appointment this week. I called her and she said she was feeling depressed. I referred her to the local CMP.',
      createdAtTime: new Date('Tuesday, June 25, 2024').getTime(),
    },
    {
      id: generateId(),
      content:
        'Sophia tried a graphic design course on OpenClassrooms and liked it. She went to her first youth support group and reports finding it helpful. I told her about the CNED correspondence program.',
      createdAtTime: new Date('Monday, June 10, 2024').getTime(),
    },
    {
      id: generateId(),
      content:
        'Introduced Sophia to some free online graphic design tutorials. She showed interest but expressed doubt about her abilities. She seemed withdrawn and reports being socially isolated.',
      createdAtTime: new Date('Tuesday, June 4, 2024').getTime(),
    },
    {
      id: generateId(),
      content:
        'Initial appointment with Sophia. She stopped going to high school after her mom’s divorce last year and has strong anxiety. She lives with her mom and doesn’t go out much. Discussed her interests and goals. She mentioned enjoying art in school and a desire to work in a creative field, possibly graphic design. She wants to finish her baccalaureate but doesn’t know how.',
      createdAtTime: new Date('Wednesday, March 22, 2024').getTime(),
    },
  ],
  actionHistoryList: [
    {
      id: generateId(),
      title: 'Schedule a therapist appointment at Maison Alfort’s medico-psychological center (CMP)',
      content:
        'The CMP offers free therapy to youth in Sophia’s situation and have experience dealing with anxiety and school refusal issues.',
      categories: ['health'],
      status: 'priority',
      durationInHours: 1,
      isBeneficiaryTask: true,
      generationId: '123',
      type: 'task',
    },
    {
      id: generateId(),
      title: 'Research CNED correspondence program',
      content:
        'CNED offers different correspondence programs that can help Sophia complete her last year of high school and register for the baccalaureate.',
      categories: ['education'],
      status: 'overdue',
      durationInHours: 1,
      isBeneficiaryTask: true,
      generationId: '123',
      type: 'task',
    },
    {
      id: generateId(),
      title: 'Attend a local youth support group through Soliguide',
      content:
        'Encourage Sophia to seek a local support group for young adults dealing with mental health issues using the Soliguide platform.',
      categories: ['health'],
      status: 'up-to-date',
      durationInHours: 4,
      isBeneficiaryTask: true,
      generationId: '123',
      type: 'task',
    },
    {
      id: generateId(),
      title: 'Explore free online graphic design courses',
      content:
        'Find beginner-level courses to help Sophia develop her interests and skills in graphic design. She may use platforms such as OpenClassrooms or Coursera.',
      categories: ['training'],
      status: 'up-to-date',
      durationInHours: 6,
      isBeneficiaryTask: true,
      generationId: '123',
      type: 'task',
    },
  ],
  messages: (sender) => [
    {
      createdAtTime: removeDays(now, 6),
      id: generateId(),
      content: 'Hi Sophia, have you scheduled your appointment with the CMP yet?',
      sender: 'coach',
      type: 'message',
      isSender: sender === 'coach',
    },
    {
      createdAtTime: removeDays(now, 5) + minute,
      id: generateId(),
      content: 'no...  I tried to call but no answer',
      sender: 'youth',
      type: 'message',
      isSender: sender === 'youth',
    },
    {
      createdAtTime: removeDays(now, 5) + 2 * minute,
      id: generateId(),
      content: 'Have you tried calling again?',
      sender: 'coach',
      type: 'message',
      isSender: sender === 'coach',
    },
    {
      createdAtTime: removeDays(now, 5) + 5 * minute,
      id: generateId(),
      content: "it's useless they never respond",
      sender: 'youth',
      type: 'message',
      isSender: sender === 'youth',
    },
    {
      createdAtTime: removeDays(now, 5) + 10 * minute,
      id: generateId(),
      content:
        "Ok, let's try something different. We have a workshop here at the center that you may find interesting.",
      sender: 'coach',
      type: 'message',
      isSender: sender === 'coach',
    },
    {
      createdAtTime: removeDays(now, 4),
      id: generateId(),
      content: "I'm not sure it will be useful",
      sender: 'youth',
      type: 'message',
      isSender: sender === 'youth',
    },
    {
      createdAtTime: removeDays(now, 4) + 5 * minute,
      id: generateId(),
      content:
        "Please try, we had very good feedback from this workshop, and the instructor is really friendly, she's actually a friend of mine",
      sender: 'coach',
      type: 'message',
      isSender: sender === 'coach',
    },
    {
      createdAtTime: removeDays(now, 3),
      id: generateId(),
      content: 'Ok I will check it out, thank you',
      sender: 'youth',
      type: 'message',
      isSender: sender === 'youth',
    },
  ],
}
const profile3: ProfileData = {
  profile: {
    id: '3',
    firstName: 'Abdel',
    lastName: 'Zekri',
    age: 22,
    education: 'Vocational Training (incomplete)',
    location: 'Aubervilliers',
    gender: 'male',
    lastUpdate: '1 week ago',
    status: 'overdue',
  },
  reports: [
    {
      id: generateId(),
      content:
        'Abdel missed our scheduled appointment. When reached by phone, he admitted to a relapse over the weekend. Encouraged him to attend an NA meeting today and rescheduled our appointment.',
      createdAtTime: new Date('Monday, July 1, 2024').getTime(),
    },
    {
      id: generateId(),
      content:
        'Abdel went to a garage near his home and the owner is open to taking him in on an apprenticeship as long as he can get re-enrolled in his CAP training program. I told Abdel he needed to have a more structured routine and not to miss this opportunity.',
      createdAtTime: new Date('Monday, June 24, 2024').getTime(),
    },
    {
      id: generateId(),
      content:
        'Abdel went to his first NA meeting and says that it helped. He seems motivated to stop smoking. He recently worked as a line cook in a restaurant but quit after a few days. He wants to go back to working with cars but is worried no one will take him in after he dropped out of his CAP.',
      createdAtTime: new Date('Thursday, June 13, 2024').getTime(),
    },
    {
      id: generateId(),
      content:
        'First meeting with Abdel. He dropped out of an automotive CAP training because of substance abuse (weed). He says it’s due to the stress of the program. He lives in a shared flat with some roommates who are also unemployed. He does small gigs to make rent but has trouble keeping a job for long. Discussed his passion for cars and how completing his training could lead to a fulfilling career. Emphasized the importance of addressing his substance abuse issues as a first step.',
      createdAtTime: new Date('Monday, June 3, 2024').getTime(),
    },
  ],
  actionHistoryList: [
    {
      id: generateId(),
      title: 'Reach out to the previous vocational school about re-enrollment',
      content: 'Contact Abdel’s former school to discuss options for completing his automotive mechanics program.',
      categories: ['education'],
      status: 'overdue',
      durationInHours: 1,
      isBeneficiaryTask: true,
      generationId: '123',
      type: 'task',
    },
    {
      id: generateId(),
      title: 'Create a structured daily routine',
      content:
        'Work with Abdel to develop a structured daily schedule that includes job search activities, NA meetings, and healthy habits.',
      categories: ['health'],
      status: 'overdue',
      durationInHours: 2,
      isBeneficiaryTask: true,
      generationId: '123',
      type: 'task',
    },
    {
      id: generateId(),
      title: 'Contact 3 garages for potential apprenticeships',
      content:
        'Encourage Abdel to go to 3 garages in Aubervilliers to apply for an apprenticeship. This can help Abdel get a more stable situation while allowing him to resume his training.',
      categories: ['education'],
      status: 'overdue',
      durationInHours: 3,
      isBeneficiaryTask: true,
      generationId: '123',
      type: 'task',
    },
    {
      id: generateId(),
      title: 'Enroll in outpatient addiction treatment program',
      content: 'Connect Abdel with a local outpatient program that specializes in young adult substance abuse.',
      categories: ['health'],
      status: 'up-to-date',
      durationInHours: 3,
      isBeneficiaryTask: true,
      generationId: '123',
      type: 'task',
    },
  ],
  messages: () => [],
}
const profile4: ProfileData = {
  profile: {
    id: '4',
    firstName: 'Emilie',
    lastName: 'Dubois',
    age: 23,
    education: 'Some college (Computer Science, dropped out)',
    location: 'Nantes',
    gender: 'female',
    lastUpdate: '1 week ago',
    status: 'up-to-date',
  },
  reports: [
    {
      id: generateId(),
      content:
        'Emilie missed our scheduled video call due to a flare-up of her condition. When we rescheduled, she shared feeling guilty and anxious about her unreliable health. Explored techniques for managing guilt and reframing her perspective on productivity.',
      createdAtTime: new Date('Monday, July 1, 2024').getTime(),
    },
    {
      id: generateId(),
      content:
        'Introduced Emilie to online communities for tech professionals with chronic illnesses. She seemed relieved to connect with others facing similar challenges and gained some practical tips for managing work with her condition.',
      createdAtTime: new Date('Wednesday, June 26, 2024').getTime(),
    },
    {
      id: generateId(),
      content:
        'Emilie completed her first coding project in the bootcamp. She was proud of her achievement but worried about maintaining this pace. Discussed the importance of pacing herself and communicating her needs to potential employers.',
      createdAtTime: new Date('Thursday, June 20, 2024').getTime(),
    },
    {
      id: generateId(),
      content:
        'Researched adaptive technologies with Emilie. She was particularly interested in voice-to-code software and ergonomic keyboards. Encouraged her to try free trials of different tools to find what works best for her.',
      createdAtTime: new Date('Friday, June 14, 2024').getTime(),
    },
    {
      id: generateId(),
      content:
        'First meeting with Emilie. She appeared hesitant and self-conscious about her situation. Discussed her passion for coding and her fears about entering the tech industry with a chronic illness. Emphasized the growing opportunities for remote work and flexible schedules in tech.',
      createdAtTime: new Date('Monday, June 3, 2024').getTime(),
    },
  ],
  actionHistoryList: [
    {
      id: generateId(),
      title: 'Work on a personal project',
      content:
        'Identify a personal project that Emilie could start to build her portfolio and showcase her front-end development skills.',
      categories: ['other'],
      status: 'up-to-date',
      durationInHours: 3,
      isBeneficiaryTask: true,
      generationId: '123',
      type: 'task',
    },
    {
      id: generateId(),
      title: 'Research ergonomic home office setup',
      content: 'Explore ergonomic solutions to help Emilie work more comfortably from home.',
      categories: ['health'],
      status: 'up-to-date',
      durationInHours: 2,
      isBeneficiaryTask: true,
      generationId: '123',
      type: 'task',
    },
    {
      id: generateId(),
      title: 'Enroll in online coding bootcamp',
      content:
        'Find a flexible, self-paced coding bootcamp that allows Emilie to build skills while managing her health.',
      categories: ['education'],
      status: 'up-to-date',
      durationInHours: 1,
      isBeneficiaryTask: true,
      generationId: '123',
      type: 'task',
    },
    {
      id: generateId(),
      title: 'Connect with disability support services',
      content:
        'Reach out to local disability support services to explore additional resources and accommodations for Emilie.',
      categories: ['health'],
      status: 'up-to-date',
      durationInHours: 1,
      isBeneficiaryTask: true,
      generationId: '123',
      type: 'task',
    },
  ],
  messages: () => [],
}
const profile5: ProfileData = {
  profile: {
    id: '5',
    firstName: 'Amira',
    lastName: 'Benali',
    age: 21,
    education: 'Bachelor’s degree in Communication',
    location: 'Paris',
    gender: 'female',
    lastUpdate: '1 week ago',
    status: 'up-to-date',
  },
  reports: [
    {
      id: generateId(),
      content:
        'Amira attended a local multicultural fair and made some connections with other young immigrants. She expressed relief at finding others in similar situations and is considering joining a weekly conversation group.',
      createdAtTime: new Date('Thursday, June 27, 2024').getTime(),
    },
    {
      id: generateId(),
      content:
        "Reviewed Amira's LinkedIn profile. She's received some connection requests but is unsure how to proceed. Discussed networking strategies and how to leverage LinkedIn for job searching in France.",
      createdAtTime: new Date('Monday, June 24, 2024').getTime(),
    },
    {
      id: generateId(),
      content:
        "Amira expressed frustration with her job search, feeling that her degree isn't valued in France. Explored options for having her Tunisian degree recognized and discussed the possibility of further education or certification in France.",
      createdAtTime: new Date('Wednesday, June 19, 2024').getTime(),
    },
    {
      id: generateId(),
      content:
        'First meeting with Amira. She appeared motivated but overwhelmed by the challenges of starting a new life in France. Discussed her background in literature and her career aspirations. Identified language skills and local work experience as primary barriers to employment.',
      createdAtTime: new Date('Tuesday, June 11, 2024').getTime(),
    },
  ],
  actionHistoryList: [
    {
      id: generateId(),
      title: 'Enroll in intensive French language course',
      content: 'Find a suitable intensive French course to help Amira improve her language skills quickly.',
      categories: ['education'],
      status: 'up-to-date',
      durationInHours: 1,
      isBeneficiaryTask: true,
      generationId: '123',
      type: 'task',
    },
    {
      id: generateId(),
      title: 'Create a LinkedIn profile',
      content:
        'Help Amira set up a professional LinkedIn profile to connect with potential employers and showcase her skills.',
      categories: ['employment'],
      status: 'up-to-date',
      durationInHours: 2,
      isBeneficiaryTask: true,
      generationId: '123',
      type: 'task',
    },
    {
      id: generateId(),
      title: 'Research cultural integration programs',
      content:
        'Find local programs or events that can help Amira connect with both the local community and other immigrants.',
      categories: ['social'],
      status: 'up-to-date',
      durationInHours: 5,
      isBeneficiaryTask: true,
      generationId: '123',
      type: 'task',
    },
    {
      id: generateId(),
      title: 'Explore internship opportunities in publishing or education',
      content:
        "Look for internships that could utilize Amira's literature background while providing French work experience.",
      categories: ['employment'],
      status: 'up-to-date',
      durationInHours: 1,
      isBeneficiaryTask: true,
      generationId: '123',
      type: 'task',
    },
  ],
  messages: () => [],
}
const profileList: ProfileData[] = [profile1, profile2, profile3, profile4, profile5]

export const mockProfileList = (): ProfileType[] => profileList.map((v) => v.profile)
